import { useEffect, useState } from 'react';

import { getIncodeVerification } from 'PrivateApis';

import { SENTRY_TAG } from '../IncodeSDKHelpers';

import { INCODE_SESSION_VERIFICATION_STATUS, INCODE_VERIFICATION_DOCUMENTS_STATUS } from './useIncodeVerificationDocuments.constants';
import { IncodeVerificationData, IncodeVerificationDocumentsStatus } from './useIncodeVerificationDocuments.types';

interface UseIncodeVerificationDocumentsProps {
  authToken?: string;
  payload?: { enquiryId?: number | null };
}

export const useIncodeVerificationDocuments = ({
  authToken,
  payload,
}: UseIncodeVerificationDocumentsProps) => {
  const [status, setStatus] = useState<IncodeVerificationDocumentsStatus>(undefined);
  const [data, setData] = useState<IncodeVerificationData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const enquiryId = payload?.enquiryId;

  useEffect(() => {
    if (!authToken || !enquiryId) {
      setIsLoading(false);
      setData(undefined);
      setStatus(undefined);
      return () => {};
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    const fetchDocuments = () => {
      setIsLoading(true);
      setStatus(undefined);

      getIncodeVerification(
        { Authorization: authToken },
        { enquiryId, type: 'seller' },
        abortController,
      )
        .then((response) => {
          if (!signal.aborted) {
            const firstVerification = response?.data?.[0] || undefined;

            if (firstVerification) {
              const { completedSteps = [], requiredSteps = [] } = firstVerification;

              const completedCount = completedSteps?.length ?? 0;
              const requiredCount = requiredSteps?.length ?? 0;

              let finalStatus;

              switch (true) {
              case completedCount === 0:
                finalStatus = INCODE_VERIFICATION_DOCUMENTS_STATUS.START;
                break;

              case completedCount === requiredCount:
                finalStatus = INCODE_VERIFICATION_DOCUMENTS_STATUS.COMPLETED;
                break;

              default:
                finalStatus = INCODE_VERIFICATION_DOCUMENTS_STATUS.CONTINUE;
              }

              setData(firstVerification);
              setStatus(finalStatus);
              return;
            }

            setData(undefined);
            setStatus(INCODE_VERIFICATION_DOCUMENTS_STATUS.START);
          }
        })
        .catch((err) => {
          if (!signal.aborted) {
            globalThis?.Sentry?.captureException?.(err, SENTRY_TAG);
            setData(undefined);
            setStatus(INCODE_VERIFICATION_DOCUMENTS_STATUS.START);
          }
        })
        .then(() => {
          if (!signal.aborted) {
            setIsLoading(false);
          }
        });
    };

    fetchDocuments();

    return () => {
      abortController.abort();
    };
  }, [authToken, enquiryId]);

  const hasActiveIncodeSession = Boolean(data?.modules)
  && data?.verificationStatus === INCODE_SESSION_VERIFICATION_STATUS.PENDING;

  return {
    data,
    hasActiveIncodeSession,
    isLoading,
    status,
  };
};
